import * as React from 'react';

import { DatePicker } from '@lightspeed/cirrus-datepicker';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { IconCalendar } from '@lightspeed/flame/Icon/Calendar';
import { IconSmallChevronDown } from '@lightspeed/flame/Icon/SmallChevronDown';
import { Text } from '@lightspeed/flame/Text';
import Moment from 'moment';

import { OverviewDataContext } from 'root/apps/PurchaseOrder/ShipmentHistory/tabs/View/contexts/OverviewData';
import { phpToMoment } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/format';

interface Props {
  datePickerFormat: string;
  lang: string;
  isDisabled: boolean;
}

export const ShippingDateReceivedDatePicker: React.FC<Props> = ({
  datePickerFormat,
  lang,
  isDisabled,
}) => {
  const { data, saveOverviewData } = React.useContext(OverviewDataContext);
  const { orderID: orderId, shipmentID: shipmentId } = data || {};
  // To force refresh as calendar will not update state until it unmounts and remounts
  // Will be removed when cirrus datepicker is fixes
  const [isForcedRefresh, setIsForcedRefresh] = React.useState(false);

  const saveShippingDateReceived = (date: Date) => {
    const dateFormat = 'YYYY-MM-DD HH:mm:ss';
    const convertDateTime = Moment(date).format(dateFormat);

    if (orderId && shipmentId) {
      saveOverviewData({
        orderId,
        shipmentId,
        payload: { receptionDate: convertDateTime },
        callback: () => {
          setIsForcedRefresh(true);
          setIsForcedRefresh(false);
        },
      });
    }
  };

  return (
    <>
      {isForcedRefresh ? null : (
        <DatePicker
          dateFormat={datePickerFormat}
          date={data?.receptionDate ? new Date(Moment(data?.receptionDate).format()) : undefined}
          locale={lang}
          isDateBlocked={(date) => date.getTime() > new Date(Moment().format('L')).getTime()}
          onSubmit={(event) => saveShippingDateReceived(event.date)}
          disabled={isDisabled}
        />
      )}
    </>
  );
};

export const ShippingInvoiceDueDatePicker: React.FC<Props> = ({
  datePickerFormat,
  lang,
  isDisabled,
}) => {
  const [invoiceDate, setInvoiceDate] = React.useState(new Date(Moment().format()));
  const [displayInvoiceDate, setDisplayInvoiceDate] = React.useState('Select Date');
  const { data, saveOverviewData } = React.useContext(OverviewDataContext);
  // To force refresh as calendar will not update state until it unmounts and remounts
  // Will be removed when cirrus datepicker is fixes
  const [isForcedRefresh, setIsForcedRefresh] = React.useState(false);

  React.useEffect(() => {
    if (data?.paymentDueDate) {
      const invoiceDueDate = new Date(Moment(data?.paymentDueDate).format());
      setInvoiceDate(invoiceDueDate);
      // Display due date if available otherwise displat "select date"
      convertDisplayDate(invoiceDueDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.paymentDueDate]);

  const { orderID: orderId, shipmentID: shipmentId } = data || {};

  const convertDisplayDate = (date: Date) => {
    const convertVendorDateFormat = phpToMoment(window.merchantos.locale.date_format);
    const convertVendorDate = Moment(date).format(convertVendorDateFormat);

    setDisplayInvoiceDate(convertVendorDate);
  };

  const saveShippingInvoiceDueDate = (date: Date) => {
    const convertToApiDateFormat = 'YYYY-MM-DD';
    const convertDateTime = Moment(date).format(convertToApiDateFormat);

    convertDisplayDate(date);

    if (orderId && shipmentId) {
      saveOverviewData({
        orderId: data!.orderID,
        shipmentId: data!.shipmentID,
        payload: { paymentDueDate: convertDateTime },
        callback: () => {
          setIsForcedRefresh(true);
          setIsForcedRefresh(false);
        },
      });
    }
  };

  return (
    <>
      {isForcedRefresh ? null : (
        <DatePicker
          dateFormat={datePickerFormat}
          locale={lang}
          date={invoiceDate}
          onSubmit={(event) => saveShippingInvoiceDueDate(event.date)}
          target={(targetRef, toggle) => {
            return (
              <Box display="inline-block" ref={targetRef}>
                <Button
                  disabled={isDisabled}
                  type="button"
                  onClick={toggle}
                  px={2}
                  data-test="shipping-invoice-due-date"
                >
                  <Flex justifyContent="space-around" alignItems="center">
                    <IconCalendar />
                    {displayInvoiceDate === 'Select Date' ? (
                      <Text mx={1} data-test="shipping-invoice-date-text">
                        Select Date
                      </Text>
                    ) : (
                      <Text
                        mx={1}
                        className="notranslate"
                        data-test="shipping-invoice-display-date"
                      >
                        {displayInvoiceDate}
                      </Text>
                    )}
                    <IconSmallChevronDown />
                  </Flex>
                </Button>
              </Box>
            );
          }}
        />
      )}
    </>
  );
};
